import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, X, Check } from "lucide-react";
import axios from "../api/axios";
import UiText from "../components/UiText";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useToast } from "../components/ui/ToastContext";

const AdminConfigurationPage = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [stageToDelete, setStageToDelete] = useState(null);
  const [deletingStage, setDeletingStage] = useState(false);
  const toast = useToast();
  const [formData, setFormData] = useState({
    key: "",
    label: "",
    order: 0,
    color: "#3b82f6",
  });

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/stages/");
      setStages(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load stages");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStage = async (e) => {
    e.preventDefault();
    if (!formData.key.trim() || !formData.label.trim()) {
      setError("Key and Label are required");
      return;
    }

    try {
      const response = await axios.post("/api/stages/", {
        ...formData,
        key: formData.key.trim(),
        label: formData.label.trim(),
      });
      setStages([...stages, response.data]);
      setShowAddModal(false);
      resetForm();
      setError(null);
      toast.success("Stage added successfully.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add stage");
    }
  };

  const handleUpdateStage = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    if (!formData.label.trim()) {
      setError("Label is required");
      return;
    }

    try {
      const response = await axios.put(`/api/stages/${editingId}`, {
        label: formData.label.trim(),
        order: formData.order,
        color: formData.color,
      });
      setStages(stages.map((s) => (s.id === editingId ? response.data : s)));
      setEditingId(null);
      resetForm();
      setError(null);
      toast.success("Stage updated successfully.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update stage");
    }
  };

  const handleDeleteStage = (stage) => {
    setStageToDelete(stage);
  };

  const confirmDeleteStage = async () => {
    if (!stageToDelete) return;
    setDeletingStage(true);
    try {
      await axios.delete(`/api/stages/${stageToDelete.id}`);
      setStages(stages.filter((s) => s.id !== stageToDelete.id));
      setError(null);
      toast.success(`Stage "${stageToDelete.label}" deleted.`);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to delete stage";
      setError(message);
      toast.error(message);
    } finally {
      setDeletingStage(false);
      setStageToDelete(null);
    }
  };

  const handleEditStage = (stage) => {
    setEditingId(stage.id);
    setFormData({
      key: stage.key,
      label: stage.label,
      order: stage.order,
      color: stage.color || "#3b82f6",
    });
  };

  const resetForm = () => {
    setFormData({
      key: "",
      label: "",
      order: 0,
      color: "#3b82f6",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <UiText variant="title" as="h1" className="text-3xl font-bold text-gray-900">
              Configuration
            </UiText>
            <UiText variant="label" as="p" className="text-gray-600 mt-1">
              Manage system stages and settings
            </UiText>
          </div>
          <button
            onClick={() => {
              setShowAddModal(true);
              resetForm();
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={18} />
            Add Stage
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Stages Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <UiText variant="section" as="h2" className="text-xl font-semibold text-gray-900">
              Deal Pipeline Stages
            </UiText>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <UiText variant="label" as="p" className="text-gray-500">
                Loading stages...
              </UiText>
            </div>
          ) : stages.length === 0 ? (
            <div className="p-8 text-center">
              <UiText variant="label" as="p" className="text-gray-500">
                No stages found. Add your first stage!
              </UiText>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Color
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Key
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Label
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Order
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stages.map((stage) => (
                    <tr key={stage.id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: stage.color || "#3b82f6" }}
                          title={stage.color}
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                        {stage.key}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {stage.label}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {stage.order}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditStage(stage)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded transition"
                            title="Edit stage"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteStage(stage)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded transition"
                            title="Delete stage"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingId) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <UiText variant="section" as="h3" className="text-lg font-semibold">
                {editingId ? "Edit Stage" : "Add New Stage"}
              </UiText>
              <button
                onClick={editingId ? cancelEdit : () => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={editingId ? handleUpdateStage : handleAddStage}>
              {/* Key Field (disabled when editing) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) =>
                    setFormData({ ...formData, key: e.target.value })
                  }
                  disabled={editingId !== null}
                  placeholder="e.g., prospecting"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              {/* Label Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) =>
                    setFormData({ ...formData, label: e.target.value })
                  }
                  placeholder="e.g., Prospecting"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Order Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      order: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Color Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    placeholder="#3b82f6"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={editingId ? cancelEdit : () => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Check size={16} />
                  {editingId ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!stageToDelete}
        title="Delete Stage"
        message={`Are you sure you want to delete the stage "${stageToDelete?.label || ""}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deletingStage}
        onConfirm={confirmDeleteStage}
        onCancel={() => setStageToDelete(null)}
      />
    </div>
  );
};

export default AdminConfigurationPage;
